const localeComparor: Intl.Collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

export class Utils {
  public static alphabeticalPattern: RegExp = /^[a-zA-ZÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž '-]*$/;
  public static structureLabelPattern: RegExp = /^[0-9a-zA-ZÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž '\-()]*$/;
  public static integerPattern: RegExp = /^[0-9]*$/;
  public static numericPattern: RegExp = /^\d*$/;
  public static siretPattern: RegExp = /^\d{14}$/;
  public static phonePattern: RegExp = /^\d{10}$/;
  public static postalCodePattern: RegExp = /^\d{5}$/;
  public static placesNumberPattern: RegExp = /^[1-9]\d*$/;
  public static seatNumberPattern: RegExp = /^\d+$/;
  public static yearPattern: RegExp = /^[1-2]\d{3}$/;
  public static lettersPattern: RegExp = /^[a-zA-Z ]*$/;

  public static normalizeStrings(obj: any): any {
    if (obj) {
      Object.keys(obj).forEach((key: string) => {
        if (typeof obj[key] === 'string' && obj[key] === '') {
          obj[key] = undefined;
        } else if (obj[key] && typeof obj[key] === 'object') {
          obj[key] = this.normalizeStrings(obj[key]);
        }
      });
    }
    return obj;
  }

  public static stringNormalizer(obj: any): any {
    if (Utils.isDefined(obj)) {
      if (Utils.isString(obj) && (obj === '' || obj.trim().length === 0)) {
        obj = undefined;
      } else if (Array.isArray(obj)) {
        obj = Array.from(obj, (value: any) => Utils.stringNormalizer(value)).filter((value: any) => Utils.isDefined(value));
      } else if (Utils.isObject(obj)) {
        Object.keys(obj).forEach((key: string) => (obj[key] = Utils.stringNormalizer(obj[key])));
      }
    }
    return obj;
  }

  public static isString(value: any): value is string {
    return typeof value === 'string';
  }

  public static isNumber(value: any): value is number {
    return typeof value === 'number';
  }

  public static isObject(value: any): boolean {
    return value != null && typeof value === 'object';
  }

  public static trim(value: string): string {
    if (Utils.isNotEmpty(value)) {
      return value.trim();
    }
    return value;
  }

  public static isDefined(value: any): boolean {
    return value !== undefined && value !== null;
  }

  public static isNotEmpty(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  public static isNil(value: any): boolean {
    return value === null || value == null;
  }

  public static isValidDate(value: any): value is Date {
    return value != null && value instanceof Date && !isNaN(value.getTime());
  }

  public static flatDeep<T = any>(arr: T[]): T[] {
    return arr.reduce((acc: T[], val: T | T[]) => acc.concat(Array.isArray(val) ? Utils.flatDeep(val) : val), []);
  }

  public static range(start: number, end: number): number[] {
    return Array(end - start + 1)
      .fill(start)
      .map((value: number, index: number) => value + index);
  }

  public static localeSort(a: string, b: string): number {
    return localeComparor.compare(a, b);
  }

  public static groupBy<T, Fn extends (item: T) => string | number>(items: T[], fn: Fn): { [K in keyof any]: T[] } {
    return items.reduce((groups: { [K in keyof any]: T[] }, item: T) => {
      const key: string | number = fn(item);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);

      return groups;
    }, {});
  }

  public static unaccent(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  public static capitalizeFirstLetters(text: string): string {
    if (!text) {
      return;
    }
    return text
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Format bytes as human-readable text.
   * E.g. 1200000 return '1.2 MB'.
   *
   * Cf. https://stackoverflow.com/a/14919494
   *
   * @param bytes Number of bytes
   * @param si True to use metric (SI) units, aka powers of 1000. False to use binary (IEC), aka powers of 1024
   * @param dp Number of decimal places to display
   *
   * @return Formatted string
   */
  public static humanFileSize(filesize: number, si: boolean = false, dp: number = 1): string {
    const thresh: number = si ? 1000 : 1024;

    if (filesize === undefined || filesize === null) {
      return <any>filesize;
    }

    if (Math.abs(filesize) < thresh) {
      return filesize + ' octet' + (filesize ? 's' : '');
    }

    const units = si ? ['ko', 'Mo', 'Go', 'To', 'Po', 'Eo', 'Zo', 'Yo'] : ['Kio', 'Mio', 'Gio', 'Tio', 'Pio', 'Eio', 'Zio', 'Yio'];
    let u: number = -1;
    const r: number = 10 ** dp;
    do {
      filesize /= thresh;
      ++u;
    } while (Math.round(Math.abs(filesize) * r) / r >= thresh && u < units.length - 1);

    return Number(filesize.toFixed(dp)) + ' ' + units[u]; // Using Number(...) to remove insignificant trailing zeros, Cf. https://stackoverflow.com/a/19623253
  }

  /**
   * Transform a phone number from '0123456789' to '01 23 45 67 89'.
   */
  public static formatPhone(phone: string, separator: string = ' '): string {
    // Transform '0123456789' to '01 23 45 67 89'
    // Cf. https://stackoverflow.com/a/8358141
    const cleaned: string = phone?.trim();
    const match: RegExpMatchArray = cleaned?.match(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);
    if (match) {
      return match.slice(1).join(separator); // Slicing because match[0] is the whole initial phone number
    }
    return phone; // Format did not succeed, so returning initial specified phone
  }

  /**
   * Shorten a string, handling ellipsis-like strings and words wrapping.
   *
   * @param str The string to shorten.
   * @param length The maximum size of the shorten string. The resulting string may be shorter.
   * @param finish An optional string to complete the string (eg. '…').
   * @param wrapWords If true, then keep final word uncut.
   * @returns The shorten string.
   */
  public static shortenString(str: string, length: number, finish: string = '…', wrapWords: boolean = false): string {
    // Special cases
    if (!str?.length || !length || length < 0 || str.length <= length) {
      return str;
    }
    if (finish.length > length) {
      return finish.slice(0, length);
    }

    // Nominal cases : do not wrap words
    if (!wrapWords) {
      return str.slice(0, length - finish.length) + finish;
    }

    // Nominal cases : wrap words
    if (str.slice(0, length).endsWith(' '.repeat(finish?.length))) {
      return str.slice(0, length - finish?.length).trimEnd() + finish;
    }

    let trimmedString: string = str.slice(0, length - finish?.length);
    trimmedString = trimmedString.slice(0, Math.min(trimmedString.length, str.slice(0, length + 1 - finish?.length).lastIndexOf(' ')));
    return trimmedString.trimEnd() + finish;
  }

  // *********************************************************************************************
  // Utility to cast in Angular template.
  // Use :
  //   In component : $myType = Utils.castTo<MyType>();
  //   In template  : $myType(myData).myProp
  // *********************************************************************************************

  public static castTo<T>(): (data) => T {
    return (data) => data as T;
  }
}
